import {
  useTrackTranscription,
  useVoiceAssistant,
  useRemoteParticipants,
} from '@livekit/components-react';
import { useMemo } from 'react';
import { useLocalMicTrack } from './useLocalMicTrack';
import { Track } from 'livekit-client';
import { PARTICIPANT_IDENTITIES } from '@widgets/Popup/TestingAgentPopup/constants/roomManager.constants';

export function useMonitoringTranscriptions() {
  const { agentTranscriptions } = useVoiceAssistant();
  const remoteParticipants = useRemoteParticipants();

  const micTrackRef = useLocalMicTrack();
  const { segments: localTranscriptions } = useTrackTranscription(micTrackRef || undefined);

  const customerParticipant = useMemo(() => {
    if (remoteParticipants.length === 0) {
      return null;
    }

    let customer = remoteParticipants.find((p) => {
      return p.identity === PARTICIPANT_IDENTITIES.CUSTOMER;
    });

    if (!customer) {
      customer = remoteParticipants.find(
        (p) =>
          p.identity.toLowerCase().includes('customer') ||
          p.identity.toLowerCase().includes('caller') ||
          p.identity.toLowerCase().includes('client'),
      );
    }

    if (!customer) {
      customer = remoteParticipants.find((p) => {
        const audioTrack = p.getTrackPublication(Track.Source.Microphone);
        return audioTrack && audioTrack.track;
      });
    }

    if (!customer && remoteParticipants.length > 0) {
      customer = remoteParticipants[0];
    }

    return customer;
  }, [remoteParticipants]);

  const remoteTrackRef = useMemo(() => {
    if (!customerParticipant) {
      return null;
    }

    const audioTrack = customerParticipant.getTrackPublication(Track.Source.Microphone);
    if (!audioTrack || !audioTrack.track) {
      return null;
    }

    return {
      participant: customerParticipant,
      source: Track.Source.Microphone,
      publication: audioTrack,
    };
  }, [customerParticipant]);

  const { segments: remoteTranscriptionSegments } = useTrackTranscription(
    remoteTrackRef || undefined,
  );

  const allTranscriptions = useMemo(() => {
    const transcriptions = [
      ...agentTranscriptions.map((segment) => ({
        ...segment,
        role: 'assistant' as const,
        participantType: 'agent' as const,
        source: 'agent' as const,
      })),
      ...localTranscriptions.map((segment) => ({
        ...segment,
        role: 'user' as const,
        participantType: 'monitor' as const,
        source: 'local' as const,
      })),
      ...remoteTranscriptionSegments.map((segment) => ({
        ...segment,
        role: 'user' as const,
        participantType: 'caller' as const,
        source: 'remote' as const,
      })),
    ];

    const sorted = transcriptions.sort((a, b) => a.firstReceivedTime - b.firstReceivedTime);

    const deduped = sorted.filter((item, index) => {
      const prev = sorted[index - 1];
      if (!prev) return true;

      const timeDiff = Math.abs(item.firstReceivedTime - prev.firstReceivedTime);
      const sameText = item.text.trim() === prev.text.trim();

      return !sameText || timeDiff > 1000;
    });

    return deduped;
  }, [agentTranscriptions, localTranscriptions, remoteTranscriptionSegments]);

  return {
    transcriptions: allTranscriptions,
    remoteParticipants,
    hasRemoteParticipants: remoteParticipants.length > 0,
    debugInfo: {
      agentCount: agentTranscriptions.length,
      localCount: localTranscriptions.length,
      remoteCount: remoteTranscriptionSegments.length,
      participantCount: remoteParticipants.length,
    },
  };
}
